// Bulk Hand to Legal from the Arrears Ageing selection bar — one reason for the batch, each file goes through the single Hand-to-Legal path.
import { useEffect, useMemo, useState } from "react";
import { Loader2, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { bulkHandToLegal, getAgeingHandlers } from "@/lib/api";

const WITH_LEGAL = new Set(["live", "paused"]);
export const alreadyWithLegal = (row) => WITH_LEGAL.has(row?.journey?.legal?.key);

export function BulkHandToLegalDialog({ rows, onClose, onDone }) {
  const [handlers, setHandlers] = useState([]);
  const [owner, setOwner] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { getAgeingHandlers("legal").then((r) => setHandlers(r.handlers || [])).catch(() => setHandlers([])); }, []);
  const eligible = useMemo(() => rows.filter((r) => !alreadyWithLegal(r)), [rows]);
  const skipped = useMemo(() => rows.filter(alreadyWithLegal), [rows]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await bulkHandToLegal(eligible.map((x) => x.case_id), reason.trim(), owner || null);
      const ldIssues = r.handed.filter((h) => h.ld_push && h.ld_push.ok === false).length;
      const msg = `${r.handed.length} file${r.handed.length === 1 ? "" : "s"} handed to Legal${owner ? ` — ${owner}` : " — Awaiting a legal handler"}`
        + (r.skipped.length ? ` · ${r.skipped.length} skipped` : "") + (r.failed.length ? ` · ${r.failed.length} FAILED` : "") + (ldIssues ? ` · ${ldIssues} LoanDisk push(es) need a manual check` : "");
      if (r.failed.length) toast.error(msg, { duration: 9000 }); else toast.success(msg);
      onDone?.(r);
    } catch (e) { toast.error("Hand to Legal failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-hand-to-legal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scale className="w-4 h-4 text-amber-600" /> Hand {eligible.length} file{eligible.length === 1 ? "" : "s"} to Legal</DialogTitle>
          <DialogDescription className="text-xs">Each file goes exactly as a single Hand to Legal does: LoanDisk → Legal, the bankruptcy route starts at Eligibility check, the reason goes on every file&apos;s timeline, and the files appear under Awaiting a legal handler.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <ul className="max-h-36 overflow-auto border rounded p-2 bg-slate-50 text-xs space-y-0.5" data-testid="bulk-hand-list">
            {eligible.map((r) => <li key={r.case_id} data-testid={`bulk-hand-row-${r.case_id}`}><span className="font-medium">{r.customer_name}</span> <span className="font-mono text-slate-500">{r.loan_application_id}</span></li>)}
            {skipped.map((r) => <li key={r.case_id} className="text-slate-400 line-through" data-testid={`bulk-hand-skip-${r.case_id}`}>{r.customer_name} <span className="font-mono">{r.loan_application_id}</span> <span className="no-underline not-italic">· already with Legal</span></li>)}
          </ul>
          <div>
            <div className="text-xs font-medium mb-1">Legal owner</div>
            <select value={owner} onChange={(e) => setOwner(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm bg-white" data-testid="bulk-hand-owner">
              <option value="">— leave for Awaiting a legal handler —</option>
              {handlers.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          <div>
            <div className="text-xs font-medium mb-1">Reason for the batch <span className="text-red-600">*</span></div>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. No contact after final demand" className="text-sm" data-testid="bulk-hand-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="bulk-hand-cancel">Cancel</Button>
          <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={submit} disabled={busy || reason.trim().length < 3 || eligible.length === 0} data-testid="bulk-hand-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Scale className="w-4 h-4 mr-1.5" />} Hand to Legal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
